import { useEffect, useState } from 'react'

interface TypewriterOptions {
  active?: boolean
  speedMs?: number
  startDelayMs?: number
  resetKey?: string | number
}

function prefersReducedMotion(): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    return false
  }
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches
}

export function useTypewriterText(
  fullText: string,
  {
    active = true,
    speedMs = 18,
    startDelayMs = 0,
    resetKey,
  }: TypewriterOptions = {},
) {
  const [displayed, setDisplayed] = useState(() => (active ? '' : fullText))

  useEffect(() => {
    if (!fullText) {
      setDisplayed('')
      return
    }

    if (!active || prefersReducedMotion()) {
      setDisplayed(fullText)
      return
    }

    let cancelled = false
    let timeoutId: number | null = null
    let index = 0

    setDisplayed('')

    const charsPerTick = Math.max(1, Math.ceil(fullText.length / 28))

    const tick = () => {
      if (cancelled) return
      index = Math.min(fullText.length, index + charsPerTick)
      setDisplayed(fullText.slice(0, index))
      if (index < fullText.length) {
        timeoutId = window.setTimeout(tick, speedMs)
      }
    }

    timeoutId = window.setTimeout(tick, startDelayMs)

    return () => {
      cancelled = true
      if (timeoutId !== null) {
        window.clearTimeout(timeoutId)
      }
    }
  }, [active, fullText, resetKey, speedMs, startDelayMs])

  return displayed
}
